import React, { useEffect, useMemo } from "react";
import { getWorkTimeTypeColor } from "../../../../models/workShitTimeSlot/workTimeType";
import Box from "@mui/material/Box";
import { MuiSwitch } from "../../../framework/muiSwitch";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import {
    useAppDispatch,
    useAppSelector,
} from "../../../../framework/customStore";
import {
    setWorkTimeTypeIds,
    toggleWorkTimeTypeId,
    toggleTelematicsFilter,
    setSelectedTelematicsFilters,
    setShowWorkingWithoutPhase,
} from "../../../../store/monitorViewFiltersSlice";
import { Base } from "../../../../framework/base";
import { VehicleDriving } from "../../../../models/vehicle/vehiclePosition";
import { getVehicleStatusText } from "../../monitorViewUtils";

interface MonitorViewWorkshiftFiltersProps {
    countByWorkTimeType: { [id: string]: number };
    countByDrivingStatus: { [key: number]: number };
}

export const MonitorViewWorktimeFilters = ({
    countByWorkTimeType,
    countByDrivingStatus,
}: MonitorViewWorkshiftFiltersProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const primaryFilter = useAppSelector(
        (state) => state.monitorViewFilters.primaryFilter
    );
    const workTimeTypes = useAppSelector(
        (state) => state.monitorViewFilters.workTimeTypes
    );
    const workTimeTypesLoading = useAppSelector(
        (state) => state.monitorViewFilters.workTimeTypesLoading
    );
    const selectedWorkTimeTypeIds = useAppSelector(
        (state) => state.monitorViewFilters.workTimeTypeIds
    );
    const selectedTelematicsFilters = useAppSelector(
        (state) => state.monitorViewFilters.selectedTelematicsFilters
    );
    const showWorkingWithoutPhase = useAppSelector(
        (state) => state.monitorViewFilters.showWorkingWithoutPhase
    );

    const typeFilters = useMemo(
        () =>
            workTimeTypes
                ? [...workTimeTypes].sort((a, b) =>
                      Base.stringCompare(a.name, b.name)
                  )
                : null,
        [workTimeTypes]
    );

    // Initialize work time type ids and telematics filters when loaded
    useEffect(() => {
        if (!selectedWorkTimeTypeIds?.length && !!typeFilters) {
            dispatch(
                setWorkTimeTypeIds(typeFilters.map((f) => f.workTimeTypeId))
            );
        }
    }, [selectedWorkTimeTypeIds, typeFilters, dispatch]);

    // Telematics filters are initialized in Redux initial state (monitorViewFiltersSlice)

    // Don't show sub-filters when "All" is selected
    if (primaryFilter === "all") {
        return null;
    }

    // "Working" sub-filters: work time type phases
    if (primaryFilter === "working") {
        if (
            workTimeTypesLoading ||
            !workTimeTypes ||
            !selectedWorkTimeTypeIds
        ) {
            return (
                <Box sx={{ padding: "6px" }}>
                    <CircularProgress size={20} />
                </Box>
            );
        }
        return (
            <Box sx={{ padding: "6px" }}>
                {typeFilters.map((w) => (
                    <MuiSwitch
                        key={w.workTimeTypeId}
                        checked={selectedWorkTimeTypeIds.includes(
                            w.workTimeTypeId
                        )}
                        label={`${w.name} (${
                            countByWorkTimeType[w.workTimeTypeId] ?? 0
                        })`}
                        color={getWorkTimeTypeColor(w.type)}
                        onChange={(_, checked) =>
                            dispatch(
                                toggleWorkTimeTypeId({
                                    id: w.workTimeTypeId,
                                    checked,
                                })
                            )
                        }
                        size="small"
                        labelSlotProps={{
                            typography: { fontSize: "0.7rem" },
                        }}
                    />
                ))}
                <MuiSwitch
                    checked={showWorkingWithoutPhase}
                    label={`${t("monitorView.workingWithoutPhase")} (${
                        countByWorkTimeType["workNoPhase"] ?? 0
                    })`}
                    color="brown"
                    onChange={(_, checked) =>
                        dispatch(setShowWorkingWithoutPhase(checked))
                    }
                    size="small"
                    labelSlotProps={{
                        typography: { fontSize: "0.7rem" },
                    }}
                />
            </Box>
        );
    }

    // "Not working" sub-filters: telematics states
    const telematicsOptions = [
        { status: VehicleDriving.Stopped, color: "gray" },
        { status: VehicleDriving.Moving, color: "green" },
        { status: VehicleDriving.Driving, color: "green" },
        { status: VehicleDriving.Inactive, color: "gray" },
    ];

    return (
        <Box sx={{ padding: "6px" }}>
            {telematicsOptions.map(({ status, color }) => (
                <MuiSwitch
                    key={status}
                    checked={selectedTelematicsFilters.includes(status)}
                    label={`${getVehicleStatusText(status, t)} (${
                        countByDrivingStatus[status] ?? 0
                    })`}
                    color={color}
                    onChange={() => dispatch(toggleTelematicsFilter(status))}
                    size="small"
                    labelSlotProps={{
                        typography: { fontSize: "0.7rem" },
                    }}
                />
            ))}
        </Box>
    );
};
